/**
 * use-products-admin — DEC-175 (quick 260731-e6h) hauler Products admin hooks.
 *
 * The read-only picker hook lives in `features/jobs/use-products.ts`
 * (VIEW_RUN_TICKETS, active-only). THIS hook is the admin list: it calls
 * `GET /v1/products?includeInactive=1` so the editor can show + reactivate
 * deactivated products. Mirrors `useJobTypesAdmin`.
 *
 * Writes (create / edit / deactivate) go through `authedFetch` directly in
 * ProductsEditor (matching JobTypesEditor's dynamic-`:id` PATCH pattern),
 * invalidating both this admin key and the read-only `PRODUCTS_QUERY_KEY`.
 */

import { useApiQuery, type WebApiOpts } from '@lodex-pro/web-shared';

/** A row from GET /v1/products (admin variant — includes `isActive`). */
export interface AdminProduct {
  readonly id: string;
  readonly name: string;
  readonly code: string;
  readonly unitOfMeasure: string;
  readonly isActive: boolean;
}

export interface ProductsAdminResponse {
  readonly products: readonly AdminProduct[];
}

export const PRODUCTS_ADMIN_QUERY_KEY = ['products', 'catalog', 'admin'] as const;

/** Common units offered in the editor dropdown. Free-text per DEC-175 — the
 *  field accepts any A–Z/0–9/underscore token up to 16 chars, so a hauler can
 *  type a unit not in this list. TANK products are BBL; LINE_ITEM products pick
 *  a real unit. */
export const COMMON_UNITS = ['BBL', 'EACH', 'TOTE', 'LOAD', 'DRUM', 'GAL', 'HOUR', 'TON'] as const;

/** Admin list — includes inactive products (MANAGE_REFERENCE_DATA). staleTime
 *  5 min, mirroring useProducts / useJobTypes for the same near-static reason. */
export function useProductsAdmin(
  apiOpts: WebApiOpts,
): ReturnType<typeof useApiQuery<ProductsAdminResponse>> {
  return useApiQuery<ProductsAdminResponse>(apiOpts, '/v1/products?includeInactive=1', {
    queryKey: PRODUCTS_ADMIN_QUERY_KEY,
    staleTime: 300_000,
  });
}
